// BA Phase 2 booking (P2.4a): the coordinator workspace and the BA's own
// invitations, over commerce's /api/v1/business-services booking routes
// (P2.1-P2.3).
//
// Owner rulings this client keeps (spec phase2-booking.md §0):
//   - D: clients have no login — there is no buyer route here. A quote is only
//     ever "accepted" by RECORDING the client's acceptance with evidence
//     (recordClientAcceptance); there is no call that accepts without it.
//   - H: every transition is a person's — a POST with If-Match: "<version>" and
//     a fresh Idempotency-Key. An assistant token gets 403 human_action_required,
//     which the screens surface as the backend says it.
//   - The booking ledger never moves money: funding evidence is a record.
//
// Booking off / not entitled = 404 on every route; screens show "not available".
import { gatewayApi } from '@/lib/hrApi';

const BS = '/api/v1/business-services';

// ── shapes (commerce internal/booking/model.go, camelCase) ────────────────────

export interface Offering {
  id: string;
  providerLegalEntityId: string;
  title: string;
  roleCategories: string[];
  locations: string[];
  bufferBeforeMin: number;
  bufferAfterMin: number;
  active: boolean;
  version: number;
}

/** A business record. No login (D). */
export interface BookingClient {
  id: string;
  legalName: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  version: number;
}

export type BookingStatus =
  | 'draft' | 'requested' | 'quoting' | 'awaiting_confirmation' | 'confirmed' | 'cancelled' | 'declined' | 'expired';

export interface Slot {
  id: string;
  bookingId: string;
  role: string;
  requiredSkills: string[];
  paidStartAt: string;
  paidEndAt: string;
  arrivalAt: string | null;
  duties: string;
  staffingGeneration: number;
  status: 'open' | 'invited' | 'filled' | 'replacement_pending';
  /** Always false in the pilot: training is not a pilot data source. */
  trainingChecked: boolean;
  version: number;
}

export interface QuoteLine {
  kind: 'worker_service' | 'management_fee' | 'expense' | 'tax';
  description: string;
  quantity: number;
  unitAmountMinor: number;
  amountMinor?: number;
}

export interface Acceptance {
  clientPersonName: string;
  clientAcceptedAt: string;
  evidenceKind: EvidenceKind;
  evidenceSha256: string;
  recordedBy: string;
  recordedAt: string;
  invalidatedAt: string | null;
}

export type QuoteStatus = 'draft' | 'issued' | 'accepted' | 'rejected' | 'expired' | 'superseded';

export interface Quote {
  id: string;
  bookingId: string;
  versionNo: number;
  status: QuoteStatus;
  currency: string;
  lines: QuoteLine[];
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  expiresAt: string | null;
  termsVersionId: string | null;
  replacementNeedsClientApproval: boolean;
  contentSha256: string | null;
  issuedAt: string | null;
  version: number;
  acceptance?: Acceptance;
}

export interface Reservation {
  state: 'hold' | 'confirmed' | 'released' | 'expired';
  occupiedStart: string;
  occupiedEnd: string;
  expiresAt: string | null;
}

export type InvitationStatus = 'draft' | 'invited' | 'accepted' | 'declined' | 'expired' | 'withdrawn';

export interface Invitation {
  id: string;
  slotId: string;
  bookingId: string;
  staffingGeneration: number;
  employeeId: string;
  profileId: string;
  feeAmountMinor: number;
  currency: string;
  paidStartAt: string;
  paidEndAt: string;
  boardingAt: string;
  returnAt: string;
  adjustmentRules: string;
  paymentTiming: string;
  termsVersionId: string | null;
  termsHash: string | null;
  expiresAt: string;
  holdUntil: string;
  status: InvitationStatus;
  invitedAt: string | null;
  acceptedAt: string | null;
  version: number;
  /** P2.2: null, or the BA's hold / confirmed reservation. */
  reservation?: Reservation | null;
}

export interface FundingEvidence {
  id: string;
  kind: FundingKind;
  evidenceSha256: string;
  note: string | null;
  recordedBy: string;
  recordedAt: string;
}

export interface Booking {
  id: string;
  offeringId: string;
  clientId: string;
  eventTitle: string;
  timezone: string;
  venue: string;
  boardingAt: string;
  departureAt: string;
  returnAt: string;
  status: BookingStatus;
  fundingStatus: 'none' | 'evidence_recorded';
  statusReason: string | null;
  version: number;
  createdAt?: string;
  // Detail view only.
  slots?: Slot[];
  quotes?: Quote[];
  invitations?: Invitation[];
  fundingEvidence?: FundingEvidence[];
}

/** A `fixed` value (a tier's chargeValue, lateCancelBaShareValue) is an INTEGER
 *  number of minor units of the terms' currency (50000 TZS = TZS 500.00);
 *  commerce refuses a fraction (400). A `percent` value is 0–100. Screens take
 *  and show normal amounts and convert at the boundary (bookingFormat). */
export interface TermsTier { noticeHoursMin: number | null; chargeBasis: 'percent' | 'fixed'; chargeValue: number | null }

/** A cancellation / no-show terms version (F). Nothing defaults; every amount is
 *  null until someone types it. */
export interface Terms {
  id: string;
  offeringId: string;
  versionNo: number;
  status: 'draft' | 'active' | 'retired';
  title: string;
  currency: string | null;
  clientCancellationTiers: TermsTier[] | null;
  lateCancelBaShareBasis: 'percent' | 'fixed' | null;
  lateCancelBaShareValue: number | null;
  baCancellationConsequence: string | null;
  noShowTerms: string | null;
  lateEarlyDelayedTerms: string | null;
  activatedAt: string | null;
  retiredAt: string | null;
  version: number;
}

// ── P2.3 confirm guard ────────────────────────────────────────────────────────

export type ReadinessKey =
  | 'confirm_enabled' | 'booking_state' | 'terms' | 'quote' | 'funding' | 'slots' | 'availability';

export interface ReadinessCheck { key: ReadinessKey | string; ok: boolean; code?: string; message?: string }

export type SlotAvailability = 'free' | 'busy' | 'unknown' | 'unstaffed';

export interface ReadinessSlot {
  slotId: string;
  role: string;
  invitationId: string | null;
  employeeId: string | null;
  availability: SlotAvailability;
  conflict: { start: string; end: string } | null;
}

export interface ConfirmReadiness {
  bookingVersion: number;
  canConfirm: boolean;
  checks: ReadinessCheck[];
  slots: ReadinessSlot[];
}

// ── the BA's view ─────────────────────────────────────────────────────────────

export interface BaCancellationTerms {
  title: string;
  lateCancelBaShareBasis: 'percent' | 'fixed' | null;
  lateCancelBaShareValue: number | null;
  currency: string | null;
  baCancellationConsequence: string | null;
  noShowTerms: string | null;
  lateEarlyDelayedTerms: string | null;
}

/** What the invited BA sees: her frozen terms and the event — never the client
 *  or the quote. */
export interface BaInvitation {
  id: string;
  status: InvitationStatus;
  version: number;
  eventTitle: string;
  venue: string;
  timezone: string;
  role: string;
  duties: string;
  feeAmountMinor: number;
  currency: string;
  paidStartAt: string;
  paidEndAt: string;
  boardingAt: string;
  returnAt: string;
  adjustmentRules: string;
  paymentTiming: string;
  termsHash: string;
  expiresAt: string;
  invitedAt: string | null;
  acceptedAt: string | null;
  trainingChecked?: boolean;
  cancellationTerms: BaCancellationTerms | null;
}

export type EvidenceKind = 'signed_quote' | 'email' | 'written_message';
export type FundingKind = 'payment_received_evidence' | 'purchase_order';

// ── transitions: If-Match + Idempotency-Key ───────────────────────────────────

/** A fresh key for ONE user action. A retry of the same action may pass the
 *  same key back (commerce replays the stored answer). */
export function newActionKey(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Very old browsers only: still unique per action.
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

/** The headers every transition sends. */
export function transitionHeaders(version: number, key: string = newActionKey()): Record<string, string> {
  return { 'If-Match': `"${version}"`, 'Idempotency-Key': key };
}

export interface ActionOpts { version: number; key?: string }

const post = async <T>(path: string, body: unknown, o: ActionOpts): Promise<T> =>
  (await gatewayApi.post<T>(`${BS}${path}`, body, { headers: transitionHeaders(o.version, o.key) })).data;

// Draft writes: any coordinator (or an assistant acting for one). PATCH needs If-Match.
const patch = async <T>(path: string, body: unknown, version: number): Promise<T> =>
  (await gatewayApi.patch<T>(`${BS}${path}`, body, { headers: { 'If-Match': `"${version}"` } })).data;
const create = async <T>(path: string, body: unknown): Promise<T> => (await gatewayApi.post<T>(`${BS}${path}`, body)).data;
const get = async <T>(path: string, params?: Record<string, string>): Promise<T> =>
  (await gatewayApi.get<T>(`${BS}${path}`, params ? { params } : undefined)).data;

// ── coordinator: offerings, clients, bookings ─────────────────────────────────

export const listOfferings = async (): Promise<Offering[]> =>
  (await get<{ offerings: Offering[] }>('/booking-offerings')).offerings ?? [];
export const createOffering = (body: {
  providerLegalEntityId: string; title: string; roleCategories: string[]; locations: string[];
  bufferBeforeMin: number; bufferAfterMin: number;
}) => create<Offering>('/booking-offerings', body);

export const listClients = async (): Promise<BookingClient[]> =>
  (await get<{ clients: BookingClient[] }>('/booking-clients')).clients ?? [];
export const createClient = (body: { legalName: string; contactName?: string; contactEmail?: string; contactPhone?: string }) =>
  create<BookingClient>('/booking-clients', body);
export const updateClient = (id: string, version: number, body: Partial<{ legalName: string; contactName: string; contactEmail: string; contactPhone: string }>) =>
  patch<BookingClient>(`/booking-clients/${id}`, body, version);

export const listBookings = async (): Promise<Booking[]> =>
  (await get<{ bookings: Booking[] }>('/service-bookings')).bookings ?? [];
export const getBooking = (id: string) => get<Booking>(`/service-bookings/${id}`);
export const createBooking = (body: {
  offeringId: string; clientId: string; eventTitle: string; timezone: string; venue: string;
  boardingAt: string; departureAt: string; returnAt: string;
}) => create<Booking>('/service-bookings', body);

export const submitBooking = (id: string, o: ActionOpts) => post<Booking>(`/service-bookings/${id}/submit`, {}, o);
export const cancelBooking = (id: string, reason: string, o: ActionOpts) =>
  post<Booking>(`/service-bookings/${id}/cancel`, { reason }, o);
export const declineBooking = (id: string, reason: string, o: ActionOpts) =>
  post<Booking>(`/service-bookings/${id}/decline`, { reason }, o);

/** Funding EVIDENCE (a record, never a collection). Multipart: file + kind + note. */
export const recordFundingEvidence = (id: string, ev: { file: File; kind: FundingKind; note?: string }, o: ActionOpts) => {
  const fd = new FormData();
  fd.append('file', ev.file);
  fd.append('kind', ev.kind);
  if (ev.note?.trim()) fd.append('note', ev.note.trim());
  return post<Booking>(`/service-bookings/${id}/funding-evidence`, fd, o);
};

// ── slots ─────────────────────────────────────────────────────────────────────

export const createSlot = (bookingId: string, body: {
  role: string; requiredSkills?: string[]; paidStartAt: string; paidEndAt: string; arrivalAt?: string; duties: string;
}) => create<Slot>(`/service-bookings/${bookingId}/slots`, body);

export const confirmReplacement = (slotId: string, o: ActionOpts) =>
  post<Slot>(`/booking-slots/${slotId}/confirm-replacement`, {}, o);

// ── quotes ────────────────────────────────────────────────────────────────────

export interface QuoteDraft {
  currency: string;
  lines: Omit<QuoteLine, 'amountMinor'>[];
  expiresAt?: string;
  termsVersionId?: string;
  replacementNeedsClientApproval: boolean;
}

export const createQuote = (bookingId: string, body: QuoteDraft) => create<Quote>(`/service-bookings/${bookingId}/quotes`, body);
export const updateQuote = (id: string, version: number, body: Partial<QuoteDraft>) => patch<Quote>(`/booking-quotes/${id}`, body, version);
export const issueQuote = (id: string, o: ActionOpts) => post<Quote>(`/booking-quotes/${id}/issue`, {}, o);
export const rejectQuote = (id: string, reason: string, o: ActionOpts) => post<Quote>(`/booking-quotes/${id}/reject`, { reason }, o);

/** Records the CLIENT's acceptance (D): their evidence, the client's person who
 *  accepted and when they did. The coordinator's action alone is never
 *  acceptance — commerce refuses this without the file. */
export const recordClientAcceptance = (quoteId: string, ev: {
  file: File; clientPersonName: string; clientAcceptedAt: string; evidenceKind: EvidenceKind;
}, o: ActionOpts) => {
  const fd = new FormData();
  fd.append('file', ev.file);
  fd.append('clientPersonName', ev.clientPersonName.trim());
  fd.append('clientAcceptedAt', ev.clientAcceptedAt);
  fd.append('evidenceKind', ev.evidenceKind);
  return post<Quote>(`/booking-quotes/${quoteId}/acceptance`, fd, o);
};

// ── terms (F) ─────────────────────────────────────────────────────────────────

export interface TermsDraft {
  offeringId?: string;
  title?: string;
  currency?: string;
  clientCancellationTiers?: TermsTier[];
  lateCancelBaShareBasis?: 'percent' | 'fixed';
  lateCancelBaShareValue?: number;
  baCancellationConsequence?: string;
  noShowTerms?: string;
  lateEarlyDelayedTerms?: string;
}

export const listTerms = async (offeringId?: string): Promise<Terms[]> =>
  (await get<{ terms: Terms[] }>('/booking-terms', offeringId ? { offeringId } : undefined)).terms ?? [];
export const createTerms = (body: TermsDraft & { offeringId: string; title: string }) => create<Terms>('/booking-terms', body);
export const updateTerms = (id: string, version: number, body: TermsDraft) => patch<Terms>(`/booking-terms/${id}`, body, version);
/** The provider's tenant_owner only (commerce enforces it). */
export const activateTerms = (id: string, o: ActionOpts) => post<Terms>(`/booking-terms/${id}/activate`, {}, o);

// ── invitations (C) ───────────────────────────────────────────────────────────

/** Every C field is required: one flat fee, the paid interval, boarding and
 *  return, adjustment rules and payment timing — plus expiry and hold-until.
 *  The BA is named ONLY by profileId (owner, 4 Nov 2026): commerce resolves the
 *  employee from employee-service's published feed. Refusals, as the backend
 *  words them: 409 ba_unavailable (not published), 422 ba_mismatch, 503 (feed
 *  unreadable). */
export interface InvitationDraft {
  profileId: string;
  feeAmountMinor: number;
  currency: string;
  paidStartAt: string;
  paidEndAt: string;
  boardingAt: string;
  returnAt: string;
  adjustmentRules: string;
  paymentTiming: string;
  termsVersionId?: string;
  expiresAt: string;
  holdUntil: string;
}

/** One bookable BA for a slot: only what staffing needs — no employee id. */
export interface BookingCandidate {
  profileId: string;
  displayName: string;
  skills: string[];
  serviceAreas: string[];
  /** Always "published": nothing else is bookable. */
  publication: 'published';
  /** Always false in the pilot: training is not a pilot data source. */
  trainingChecked: boolean;
  /** For the booking's window. "unknown" is never free. */
  availability: 'free' | 'busy' | 'unknown';
  conflict: { start: string; end: string } | null;
}

export interface SlotCandidates {
  slotId: string;
  window: { start: string; end: string };
  candidates: BookingCandidate[];
}

/** The provider's published BAs for this slot (services:booking:manage only —
 *  not the portfolio reviewer's permission). 503 when the catalogue can't be
 *  read: the caller shows the message, never an empty list. Read only; it
 *  reads availability live, so it can take a few seconds. */
export const listSlotCandidates = async (slotId: string): Promise<SlotCandidates> => {
  const d = await get<SlotCandidates>(`/booking-slots/${slotId}/candidates`);
  return { ...d, candidates: d.candidates ?? [] };
};

export const createInvitation = (slotId: string, body: InvitationDraft) =>
  create<Invitation>(`/booking-slots/${slotId}/invitations`, body);
export const updateInvitation = (id: string, version: number, body: Partial<InvitationDraft>) =>
  patch<Invitation>(`/booking-invitations/${id}`, body, version);
export const sendInvitation = (id: string, o: ActionOpts) => post<Invitation>(`/booking-invitations/${id}/send`, {}, o);
export const withdrawInvitation = (id: string, reason: string, o: ActionOpts) =>
  post<Invitation>(`/booking-invitations/${id}/withdraw`, { reason }, o);

// ── confirm (P2.3) ────────────────────────────────────────────────────────────

/** Read only; reads leave + assignments live, so it can take a few seconds. */
export const getConfirmReadiness = (bookingId: string) =>
  get<ConfirmReadiness>(`/service-bookings/${bookingId}/confirm-readiness`);
export const confirmBooking = (bookingId: string, o: ActionOpts) => post<Booking>(`/service-bookings/${bookingId}/confirm`, {}, o);

// ── the BA's own invitations (ba:profile:self) ────────────────────────────────

export const listMyInvitations = async (): Promise<BaInvitation[]> =>
  (await get<{ invitations: BaInvitation[] }>('/me/booking-invitations')).invitations ?? [];
export const getMyInvitation = (id: string) => get<BaInvitation>(`/me/booking-invitations/${id}`);
/** Accepts EXACTLY the terms she read: termsHash is the one displayed. */
export const acceptMyInvitation = (id: string, termsHash: string, o: ActionOpts) =>
  post<BaInvitation>(`/me/booking-invitations/${id}/accept`, { termsHash }, o);
export const declineMyInvitation = (id: string, o: ActionOpts) =>
  post<BaInvitation>(`/me/booking-invitations/${id}/decline`, {}, o);

// ── error helpers ─────────────────────────────────────────────────────────────

export const errorStatus = (e: unknown): number | undefined => (e as { response?: { status?: number } })?.response?.status;
export const errorCode = (e: unknown): string | undefined =>
  (e as { response?: { data?: { code?: string } } })?.response?.data?.code;
